import React, { useState, useEffect } from 'react';
import { useConfig } from '../config/ConfigContext';
import DesktopLayout from '../layouts/DesktopLayout';
import MobileLayout from '../layouts/MobileLayout';
import useMediaQuery from '../hooks/useMediaQuery';
import PrayerTimesWidget from '../components/home/PrayerTimesWidget';
import EventsSection from '../components/home/EventsSection';
import StoriesBar from '../components/home/StoriesBar';
import AnnouncementsCarousel from '../components/home/AnnouncementsCarousel';
import AnnouncementTicker from '../components/home/AnnouncementTicker';
import IslamicOccasionBanner from '../components/home/IslamicOccasionBanner';
import { fetchOccasions, fetchCenterCalendar, getTodaysOccasion, getMonthOccasionMap } from '../services/islamicOccasionsService';
import ShortsPanel from '../components/ShortsPanel';
import VideoHighlights from '../components/home/VideoHighlights';
import PublicationInsight from '../components/home/PublicationInsight';
import LectureSeriesGrid from '../components/home/LectureSeriesGrid';
import VideoCarousel from '../components/home/VideoCarousel';
import ImpactCounters from '../components/home/ImpactCounters';
import SupportCTA from '../components/home/SupportCTA';
import VisionSection from '../components/home/VisionSection';
import AboutUsCard from '../components/home/AboutUsCard';
import UnifiedImpactSupport from '../components/home/UnifiedImpactSupport';
import FloatingActionButton from '../components/home/FloatingActionButton';
import KeyTakeawaysSection from '../components/home/KeyTakeawaysSection';
import { metaTagsService } from '../services/metaTagsService';
import { fetchLectureData } from '../services/googleSheets';
import useShortsData from '../components/Shorts/hooks/useShortsData';
import useClipsData from '../components/LectureClips/hooks/useClipsData';
import calendarService from '../services/calendarService';
import communityService from '../services/communityService';
import prayerTimesService from '../services/prayerTimesService';
import { getEasternTime, isToday, isFuture } from '../services/timezoneService';
import { videosService } from '../services/videosService';
import { useLiveStatus } from '../contexts/LiveStatusContext';
import { dailyShuffle, getDailyRandomSubset, getDailyMixedSelection } from '../utils/dailyRandom';

/**
 * Rich Homepage Component
 * Displays a dynamic dashboard with multiple content sections
 * Only shows when 2+ community features are enabled
 */
const RichHomePage = () => {
  const { config, theme, loading: configLoading, error: configError } = useConfig();
  const isMobileView = useMediaQuery('(max-width: 768px)');
  const { onActiveEventChange } = useLiveStatus();

  // Fetch shorts data using the same hook as ShortsPanel
  const {
    shorts: shortsFromHook,
    loading: shortsLoading,
    error: shortsError
  } = useShortsData(config?.google_sheet_id, 'all', config);

  // Fetch lecture clips for StoriesBar badge
  const clipsEnabled = config?.lecture_clips_enabled === true;
  const { clips: lectureClips = [] } = useClipsData('all', clipsEnabled ? config?.center_id : null);
  
  // State management
  const [state, setState] = useState({
    prayerTimes: null,
    currentEvent: null,
    upcomingEvents: [],
    announcements: [],
    lectures: [],
    videos: [],
    popularVideos: [],
    recentLives: [],
    shorts: [],
    impactStats: null,
    todayOccasionData: null,
    occasionMap: null,
    loading: {
      prayers: true,
      events: true,
      announcements: true,
      content: true
    },
    errors: {}
  });

  // Fetch Islamic occasions data (if feature enabled)
  useEffect(() => {
    if (!config?.islamic_occasions_enabled || !config?.islamic_occasions_sheet_id) return;
    const centerId = config?.center_id || config?.id;
    const loadOccasions = async () => {
      try {
        const [occasions, calendar] = await Promise.all([
          fetchOccasions(config.islamic_occasions_sheet_id),
          fetchCenterCalendar(config.google_sheet_id, centerId),
        ]);
        if (occasions.length > 0 && calendar.length > 0) {
          const todayData = getTodaysOccasion(calendar, occasions);
          // Build occasion map for current month (for event cards)
          const now = new Date();
          const map = getMonthOccasionMap(now.getFullYear(), now.getMonth() + 1, calendar, occasions);
          setState(prev => ({
            ...prev,
            todayOccasionData: todayData,
            occasionMap: map,
          }));
          // Expose data for PrayerTimesWidget
          window.__islamicCalendarData = calendar;
          window.__islamicOccasionsData = occasions;
        }
      } catch (err) {
        console.warn('[RichHomePage] Failed to load Islamic occasions:', err.message);
      }
    };
    loadOccasions();
  }, [config?.islamic_occasions_enabled, config?.islamic_occasions_sheet_id, config?.google_sheet_id]);

  // Feature detection - determines if we should show rich homepage
  const shouldShowRichHomepage = () => {
    if (!config) return false;
    
    const features = [
      config?.video_options_enabled,
      config?.community_features?.calendar?.enabled,
      config?.community_features?.announcements
    ];
    
    const enabledCount = features.filter(Boolean).length;
    
    // Need at least 2 community features for rich homepage
    return enabledCount >= 2;
  };

  // Check which features are enabled
  const featuresEnabled = {
    prayers: true, // Always show prayer times for now
    events: config?.community_features?.calendar?.enabled === true || config?.islamic_occasions_enabled === true,
    announcements: config?.community_features?.announcements?.enabled === true || config?.community_features?.announcements === true,
    videos: config?.video_options_enabled === true,
    support: config?.support_enabled === true,
    about: config?.about_us_enabled !== false
  };
  
  // Debug logging
  console.log('[RichHomePage] Features enabled:', featuresEnabled);
  console.log('[RichHomePage] Config:', config);
  console.log('[RichHomePage] State:', state);

  // Update meta tags
  useEffect(() => {
    if (config && !configLoading) {
      const logoUrl = config.logo_url || `${window.location.origin}/images/penta-lectures-logo.png`;
      const description = config.description || 'Your community hub for events, lectures, and spiritual content';
      
      metaTagsService.updateMetaTags({
        title: `${config.name || 'Community Center'} - Home`,
        description: description,
        image: logoUrl,
        url: window.location.origin,
        type: 'website'
      });
    }

    return () => {
      if (config) {
        metaTagsService.resetToDefaults();
      }
    };
  }, [config, configLoading]);

  // Load all data sections
  useEffect(() => {
    if (!config || configLoading) return;

    const loadData = async () => {
      // Load events if enabled
      if (featuresEnabled.events) {
        loadEvents();
      }

      // Load announcements if enabled
      if (featuresEnabled.announcements) {
        loadAnnouncements();
      }

      // Load lectures and videos
      if (featuresEnabled.videos) {
        loadLectures();
      }

      // Load prayer times if enabled
      if (featuresEnabled.prayers) {
        loadPrayerTimes();
      }

      // Load impact stats
      loadImpactStats();
    };

    loadData();
  }, [config, configLoading]);

  // Trigger live detection when a "Happening Now" event is active
  useEffect(() => {
    const hasActive = !!state.currentEvent;
    const eventId = state.currentEvent?.id;
    if (onActiveEventChange && config?.youtube_channel_url) {
      onActiveEventChange(hasActive, eventId);
    }
  }, [state.currentEvent?.id, onActiveEventChange, config?.youtube_channel_url]);

  // Data loading functions
  const loadEvents = async () => {
    try {
      console.log('[RichHomePage] Loading events for center_id:', config.center_id);
      setState(prev => ({
        ...prev,
        loading: { ...prev.loading, events: true }
      }));

      // Load events from calendar service
      const events = await calendarService.getUpcomingEvents(config.center_id);
      console.log('[RichHomePage] Events loaded:', events);
      
      // Get current Eastern time
      const now = await getEasternTime();
      console.log('[RichHomePage] Current Eastern time:', now);
      
      // Filter out stale events (ended more than 2 hours ago)
      const activeEvents = events?.filter(event => !calendarService.isEventStale(event)) || [];
      console.log('[RichHomePage] Active events after filtering stale:', activeEvents.length);
      
      // Find current/live event
      const currentEvent = activeEvents.find(event => {
        const [startYear, startMonth, startDay] = event.start_date.split('-');
        const [endYear, endMonth, endDay] = (event.end_date || event.start_date).split('-');
        
        // Parse start datetime
        let start;
        if (event.start_time) {
          const [startHour, startMin] = event.start_time.split(':');
          start = new Date(startYear, startMonth - 1, startDay, parseInt(startHour), parseInt(startMin));
        } else {
          start = new Date(startYear, startMonth - 1, startDay, 0, 0);
        }
        
        // Parse end datetime
        let end;
        if (event.end_time) {
          const [endHour, endMin] = event.end_time.split(':');
          end = new Date(endYear, endMonth - 1, endDay, parseInt(endHour), parseInt(endMin));
        } else {
          end = new Date(endYear, endMonth - 1, endDay, 23, 59, 59);
        }
        
        return start <= now && end >= now;
      });
      
      // Get upcoming events (including today's future events)
      const upcomingEvents = activeEvents
        .filter(e => {
          // Skip current/live event
          if (e?.id === currentEvent?.id) return false;
          // Include if event is upcoming (hasn't ended yet)
          return calendarService.isEventUpcoming(e);
        })
        .sort((a, b) => {
          // Sort by start datetime - parse dates properly
          const [aYear, aMonth, aDay] = a.start_date.split('-');
          const [bYear, bMonth, bDay] = b.start_date.split('-');
          
          let aStart, bStart;
          
          if (a.start_time) {
            const [aHour, aMin] = a.start_time.split(':');
            aStart = new Date(aYear, aMonth - 1, aDay, parseInt(aHour), parseInt(aMin));
          } else {
            aStart = new Date(aYear, aMonth - 1, aDay, 0, 0);
          }
          
          if (b.start_time) {
            const [bHour, bMin] = b.start_time.split(':');
            bStart = new Date(bYear, bMonth - 1, bDay, parseInt(bHour), parseInt(bMin));
          } else {
            bStart = new Date(bYear, bMonth - 1, bDay, 0, 0);
          }
          
          return aStart - bStart;
        })
        .slice(0, 3); // Take first 3 events
      
      setState(prev => ({
        ...prev,
        currentEvent,
        upcomingEvents,
        loading: { ...prev.loading, events: false }
      }));
    } catch (error) {
      console.error('[RichHomePage] Error loading events:', error);
      setState(prev => ({
        ...prev,
        errors: { ...prev.errors, events: error.message },
        loading: { ...prev.loading, events: false }
      }));
    }
  };

  const loadAnnouncements = async () => {
    try {
      console.log('[RichHomePage] Loading announcements for center_id:', config.center_id);
      setState(prev => ({
        ...prev,
        loading: { ...prev.loading, announcements: true }
      }));

      // Load announcements from community service
      const announcements = await communityService.getAnnouncements(config.center_id);
      console.log('[RichHomePage] Announcements loaded:', announcements);
      
      // Sort by pinned first, then by date
      const sorted = (announcements || [])
        .sort((a, b) => {
          if (a.is_pinned && !b.is_pinned) return -1;
          if (!a.is_pinned && b.is_pinned) return 1;
          return new Date(b.created_at) - new Date(a.created_at);
        })
        .slice(0, 5);
      
      setState(prev => ({
        ...prev,
        announcements: sorted,
        loading: { ...prev.loading, announcements: false }
      }));
    } catch (error) {
      console.error('[RichHomePage] Error loading announcements:', error);
      setState(prev => ({
        ...prev,
        errors: { ...prev.errors, announcements: error.message },
        loading: { ...prev.loading, announcements: false }
      }));
    }
  };

  const loadLectures = async () => {
    try {
      console.log('[RichHomePage] Loading content from sheet and database');
      setState(prev => ({
        ...prev,
        loading: { ...prev.loading, content: true }
      }));

      // Load lectures from Google Sheets
      const allContent = await fetchLectureData(config.google_sheet_id);
      console.log('[RichHomePage] Google Sheets content loaded:', allContent);
      
      // Get center ID for video fetching
      let centerId = config.center_id;
      if (!centerId && config.domain) {
        const centerData = await videosService.getCenterIdByDomain(config.domain);
        centerId = centerData?.id;
      }
      
      if (!centerId) {
        console.error('[RichHomePage] No center ID available for video fetching');
      }
      
      // Load videos from Supabase using videosService (same as VideosPage)
      let allVideos = [];
      let recentLives = [];
      
      if (centerId) {
        // Fetch regular videos
        const videosResult = await videosService.fetchVideos(centerId, 'video', 0, 50);
        allVideos = videosResult.videos || [];
        console.log('[RichHomePage] Loaded videos from Supabase:', allVideos.length);
        
        // Fetch live videos (limited to 10)
        const livesResult = await videosService.fetchVideos(centerId, 'live', 0, 10);
        recentLives = livesResult.videos || [];
        console.log('[RichHomePage] Loaded live videos from Supabase:', recentLives.length);
      }
      
      // Get daily random videos for discovery (Stories bar already shows recent)
      // Use pure randomization since Stories bar handles chronological content
      const popularVideos = getDailyRandomSubset(allVideos, 8);
      
      console.log('[RichHomePage] Daily random videos selected:', 
        popularVideos.length, 'from', allVideos.length, 'total videos');
      
      // Get latest videos for stories (most recent)
      const videos = [...allVideos]
        .sort((a, b) => new Date(b.published_at || b.publishedAt) - new Date(a.published_at || a.publishedAt))
        .slice(0, 5);
      
      // Process lectures from Google Sheets with daily randomization
      const allLectures = (allContent || [])
        .filter(item => item.fullVideoUrl || item.summaryVideoUrl);
      
      // Use daily random selection to show different lectures each day
      const lectures = getDailyRandomSubset(allLectures, 6);
      
      // Debug which lectures are being shown
      console.log('[RichHomePage] Daily random lectures being displayed:', 
        lectures.map(l => ({
          id: l.lectureId,
          title: l.title,
          speaker: l.speaker,
          hasFullVideo: !!l.fullVideoUrl,
          hasSummary: !!l.summaryVideoUrl
        }))
      );
      console.log(`[RichHomePage] Selected 6 from ${allLectures.length} total lectures`);
      
      // Shorts will be fetched using the useShortsData hook separately
      
      console.log('[RichHomePage] Content categorized - Lectures:', lectures.length, 
        'Popular Videos:', popularVideos.length, 'Recent Lives:', recentLives.length);
      
      
      setState(prev => ({
        ...prev,
        lectures: lectures.slice(0, 6),
        videos,
        popularVideos,  // Now contains recommended videos
        recentLives,
        loading: { ...prev.loading, content: false }
      }));
    } catch (error) {
      console.error('[RichHomePage] Error loading lectures:', error);
      setState(prev => ({
        ...prev,
        errors: { ...prev.errors, content: error.message },
        loading: { ...prev.loading, content: false }
      }));
    }
  };

  const loadPrayerTimes = async () => {
    try {
      setState(prev => ({
        ...prev,
        loading: { ...prev.loading, prayers: true }
      }));

      // Use center's zip_code from Supabase, fall back to prayer_settings from GitHub config
      const zipCode = config?.zip_code || config?.prayer_settings?.zip_code || '07039';
      console.log('[RichHomePage] Loading prayer times for zip:', zipCode);
      
      // Clear old cache if it exists without required fields
      const cacheKey = `prayerTimes_${zipCode}_${new Date().toISOString().split('T')[0]}`;
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        try {
          const data = JSON.parse(cached);
          if (!data.sunset || !data.cacheVersion) {
            console.log('[RichHomePage] Clearing old cache without sunset/version');
            localStorage.removeItem(cacheKey);
          }
        } catch (e) {
          console.log('[RichHomePage] Error checking cache, clearing it:', e);
          localStorage.removeItem(cacheKey);
        }
      }
      
      const times = await prayerTimesService.fetchShiaPrayerTimes(zipCode);
      console.log('[RichHomePage] Prayer times loaded:', {
        hasSunset: !!times?.sunset,
        hasMaghrib: !!times?.maghrib,
        sunset: times?.sunset,
        maghrib: times?.maghrib,
        times
      });
      
      setState(prev => ({
        ...prev,
        prayerTimes: times,
        loading: { ...prev.loading, prayers: false }
      }));
    } catch (error) {
      console.error('[RichHomePage] Error loading prayer times:', error);
      setState(prev => ({
        ...prev,
        errors: { ...prev.errors, prayers: error.message },
        loading: { ...prev.loading, prayers: false }
      }));
    }
  };

  const loadImpactStats = async () => {
    try {
      // Load impact stats from calendar service
      if (config?.community_features?.calendar?.enabled && config?.center_id) {
        const year = new Date().getFullYear();
        const events = await calendarService.getAllEvents(config.center_id);
        
        console.log('[loadImpactStats] All events fetched:', events.length);
        console.log('[loadImpactStats] Sample events:', events.slice(0, 3));
        
        // Filter events for current year
        const yearEvents = events.filter(e => {
          const eventYear = new Date(e.start_date).getFullYear();
          return eventYear === year;
        });
        
        console.log('[loadImpactStats] Filtered events for year', year, ':', yearEvents.length);
        
        // Calculate stats
        const majalis = yearEvents.filter(e => 
          e.event_type === 'majlis' || e.title?.toLowerCase().includes('majlis')
        ).length;
        
        const schoolSessions = yearEvents.filter(e => 
          e.event_type === 'school' || e.title?.toLowerCase().includes('school')
        ).length;
        
        const programs = yearEvents.length;
        
        console.log('[loadImpactStats] Final counts - programs:', programs, 'majalis:', majalis, 'school:', schoolSessions);
        
        // Get annual projection
        const projectionData = await calendarService.calculateAnnualProjection(config.center_id);
        console.log('[loadImpactStats] Projection data:', projectionData);
        
        setState(prev => ({
          ...prev,
          impactStats: {
            majalis,
            programs,
            schoolSessions,
            annualProjection: projectionData
          }
        }));
      } else {
        // Use default stats if calendar not enabled
        setState(prev => ({
          ...prev,
          impactStats: {
            majalis: 67,
            programs: 234,
            schoolSessions: 45,
            annualProjection: null
          }
        }));
      }
    } catch (error) {
      console.error('Error loading impact stats:', error);
      // Use default stats on error
      setState(prev => ({
        ...prev,
        impactStats: {
          majalis: 67,
          programs: 234,
          schoolSessions: 45,
          annualProjection: null
        }
      }));
    }
  };

  // Personalization functions
  const trackUserActivity = {
    saveLastVisit: () => {
      try {
        localStorage.setItem('lastVisit', Date.now().toString());
      } catch (e) {
        console.error('Could not save to localStorage:', e);
      }
    },
    
    saveLastWatched: (lectureId, timestamp, duration) => {
      try {
        localStorage.setItem('lastWatchedLecture', JSON.stringify({
          lectureId,
          timestamp,
          duration,
          watchedAt: Date.now()
        }));
      } catch (e) {
        console.error('Could not save to localStorage:', e);
      }
    },
    
    markAnnouncementViewed: (id) => {
      try {
        const viewed = JSON.parse(localStorage.getItem('viewedAnnouncements') || '[]');
        if (!viewed.includes(id)) {
          viewed.push(id);
          localStorage.setItem('viewedAnnouncements', JSON.stringify(viewed));
        }
      } catch (e) {
        console.error('Could not save to localStorage:', e);
      }
    }
  };

  // Save last visit on mount
  useEffect(() => {
    trackUserActivity.saveLastVisit();
  }, []);
  
  // Handle payment success from Stripe
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentSuccess = urlParams.get('payment_success');
    const amount = urlParams.get('amount');
    
    if (paymentSuccess === 'true') {
      // Show success message
      const showSuccessMessage = () => {
        const successDiv = document.createElement('div');
        successDiv.style.cssText = `
          position: fixed;
          top: 20px;
          left: 50%;
          transform: translateX(-50%);
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
          padding: 16px 24px;
          border-radius: 12px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.2);
          z-index: 99999;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          font-size: 16px;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 12px;
          animation: slideDown 0.4s ease-out;
        `;
        
        successDiv.innerHTML = `
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
          <span>Thank you for your ${amount ? '$' + amount : ''} donation!</span>
        `;
        
        document.body.appendChild(successDiv);
        
        // Add animation styles
        const style = document.createElement('style');
        style.textContent = `
          @keyframes slideDown {
            from {
              opacity: 0;
              transform: translateX(-50%) translateY(-20px);
            }
            to {
              opacity: 1;
              transform: translateX(-50%) translateY(0);
            }
          }
        `;
        document.head.appendChild(style);
        
        // Remove message after 5 seconds
        setTimeout(() => {
          successDiv.style.animation = 'slideUp 0.4s ease-out';
          successDiv.style.animationFillMode = 'forwards';
          setTimeout(() => {
            if (document.body.contains(successDiv)) {
              document.body.removeChild(successDiv);
            }
            if (document.head.contains(style)) {
              document.head.removeChild(style);
            }
          }, 400);
        }, 5000);
      };
      
      showSuccessMessage();
      
      // Clear URL parameters
      const newUrl = window.location.pathname;
      window.history.replaceState(null, '', newUrl);
    }
  }, []);
  
  // Update shorts state when fetched from hook
  useEffect(() => {
    if (shortsFromHook && shortsFromHook.length > 0) {
      setState(prev => ({
        ...prev,
        shorts: shortsFromHook
      }));
      console.log('[RichHomePage] Updated shorts from hook:', shortsFromHook.length);
    }
  }, [shortsFromHook]);

  // Determine which layout to use
  const Layout = isMobileView ? MobileLayout : DesktopLayout;

  // Loading state
  if (configLoading) {
    return (
      <Layout>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          fontSize: '18px',
          color: theme?.colors?.primary
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: '16px' }}>Loading homepage...</div>
            <div style={{
              width: '40px',
              height: '40px',
              margin: '0 auto',
              border: `4px solid ${theme?.colors?.lightGray}`,
              borderTopColor: theme?.colors?.primary,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
          </div>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </Layout>
    );
  }

  // Error state
  if (configError) {
    return (
      <Layout>
        <div style={{
          padding: '40px',
          textAlign: 'center',
          color: theme?.colors?.error
        }}>
          <h2>Error Loading Homepage</h2>
          <p>{configError.message || configError}</p>
          <button
            onClick={() => window.location.reload()}
            style={{
              backgroundColor: theme?.colors?.primary,
              color: 'white',
              border: 'none',
              padding: '10px 16px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px',
              marginTop: '16px'
            }}
          >
            Retry
          </button>
        </div>
      </Layout>
    );
  }

  // Check if we should show rich homepage
  if (!shouldShowRichHomepage()) {
    // Redirect to simple homepage
    console.log('Not enough features enabled for rich homepage, falling back to simple view');
    return null; // Let the router handle fallback
  }

  return (
    <Layout>
      {/* Mobile Stories Bar - Only on mobile at top */}
      {isMobileView && (
        <StoriesBar
          announcements={state.announcements}
          currentEvent={state.currentEvent}
          latestLecture={state.lectures[0]}
          videos={state.videos}
          recentLives={state.recentLives}
          shorts={state.shorts}
          lectureClips={lectureClips}
          onStoryClick={(type, data) => {
            console.log('Story clicked:', type, data);
            // Handle story clicks
          }}
        />
      )}

      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: isMobileView ? '16px' : '32px 24px',
        backgroundColor: theme?.colors?.background || '#f8f9fa',
        minHeight: 'calc(100vh - 160px)'
      }}>
        {/* Prayer Times Widget - Top of page if enabled */}
        {featuresEnabled.prayers && (
          <PrayerTimesWidget 
            config={config}
            theme={theme}
            prayerTimes={state.prayerTimes}
            loading={state.loading.prayers}
            error={state.errors.prayers}
          />
        )}

        {/* Announcement Ticker — slim bar, auto-rotates, tap to expand */}
        {featuresEnabled.announcements && state.announcements.length > 0 && (
          <AnnouncementTicker
            announcements={state.announcements}
            theme={theme}
            isMobile={isMobileView}
          />
        )}

        {/* Islamic Occasion Banner — shows today's occasion if any */}
        {state.todayOccasionData && state.todayOccasionData.occasions.length > 0 && (
          <IslamicOccasionBanner
            todayData={state.todayOccasionData}
            theme={theme}
            isMobile={isMobileView}
          />
        )}

        {/* Events Section - Live/Upcoming */}
        {featuresEnabled.events && (
          <EventsSection
            currentEvent={state.currentEvent}
            upcomingEvents={state.upcomingEvents}
            loading={state.loading.events}
            error={state.errors.events}
            theme={theme}
            isMobile={isMobileView}
            occasionMap={state.occasionMap}
          />
        )}

        {/* Key Insights Section - Educational insights */}
        {featuresEnabled.videos && (
          <KeyTakeawaysSection
            googleSheetId={config.google_sheet_id}
            theme={theme}
            isMobile={isMobileView}
          />
        )}

        {/* Shorts Section - 3rd position for better engagement */}
        {featuresEnabled.videos && (
          <ShortsPanel googleSheetId={config.google_sheet_id} />
        )}

        {/* Publication Insight - Daily rotating insight from publications (only if center has publications configured) */}
        {config?.publications_sheet_id && (
          <PublicationInsight
            googleSheetId={config.publications_sheet_id}
            theme={theme}
            isMobile={isMobileView}
          />
        )}

        {/* Video Highlights - Popular Videos and Recent Lives */}
        {featuresEnabled.videos && (
          <VideoHighlights
            popularVideos={state.popularVideos}
            recentLives={state.recentLives}
            loading={state.loading.content}
          />
        )}

        {/* Unified Impact & Support Section - Moved BEFORE Lectures */}
        {isMobileView ? (
          // Mobile: Show unified component
          featuresEnabled.support && state.impactStats && (
            <UnifiedImpactSupport
              config={config}
              stats={state.impactStats}
              theme={theme}
              isMobile={isMobileView}
            />
          )
        ) : (
          // Desktop: Also show unified component for consistency
          featuresEnabled.support && state.impactStats && (
            <UnifiedImpactSupport
              config={config}
              stats={state.impactStats}
              theme={theme}
              isMobile={isMobileView}
            />
          )
        )}

        {/* Lecture Series Grid */}
        {featuresEnabled.videos && (
          <LectureSeriesGrid
            lectures={state.lectures}
            loading={state.loading.content}
            theme={theme}
            isMobile={isMobileView}
            onLectureWatch={trackUserActivity.saveLastWatched}
          />
        )}

        {/* Video Carousel - Only show on desktop */}
        {featuresEnabled.videos && !isMobileView && (
          <VideoCarousel
            videos={state.videos}
            theme={theme}
            isMobile={isMobileView}
          />
        )}

        {/* About Us Section */}
        {featuresEnabled.about && (
          <AboutUsCard
            config={config}
            theme={theme}
            isMobile={isMobileView}
          />
        )}
      </div>

      <FloatingActionButton 
        centerName={config?.name || 'Community Center'}
        config={config}
        theme={theme}
      />
    </Layout>
  );
};

export default RichHomePage;